/**
 * StatusChangePromptDialog — iter358.
 *
 * Mounted once in App.js. Listens for `crm-status-changed` CustomEvent and
 * shows a quick prompt asking whether to send SMS / e-mail to the customer.
 *
 * The prompt respects admin-level toggles fetched from
 *   GET /api/v1/status-prompts/settings
 *
 * Event detail shape (all fields optional except module+entityId):
 *   {
 *     module:       "quotes" | "submissions" | "contracts" | "claims" | "service_orders" | "projects" | "invoices",
 *     entityId:     string,
 *     newStatus:    string,
 *     oldStatus?:   string,
 *     entityLabel?: string,         // human label e.g. "Nabídka QUO-2026-0008"
 *     customer?: {
 *       name?: string,
 *       phone?: string,
 *       email?: string,
 *     }
 *   }
 *
 * SMS button: dispatches `crm-open-sms` (handled by GlobalSmsDialog).
 * E-mail button: opens default mail client via mailto: with prefilled subject.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, Mail, X, Bell } from "lucide-react";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const MODULE_LABELS = {
  quotes: "Nabídka",
  submissions: "Poptávka",
  contracts: "Smlouva",
  claims: "Reklamace",
  service_orders: "Servis",
  projects: "Projekt",
  invoices: "Faktura",
};

export default function StatusChangePromptDialog() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const settingsRef = useRef(null);
  const settingsLoadedAt = useRef(0);

  // Fetch and cache settings (5 min TTL).
  const getSettings = async () => {
    const now = Date.now();
    if (settingsRef.current && now - settingsLoadedAt.current < 5 * 60_000) {
      return settingsRef.current;
    }
    try {
      const r = await axios.get(`${API_V1}/status-prompts/settings`);
      settingsRef.current = r.data;
      settingsLoadedAt.current = now;
      return r.data;
    } catch {
      // Default to disabled on error so we never spam.
      return { sms_enabled: false, email_enabled: false, modules: {} };
    }
  };

  useEffect(() => {
    const handler = async (e) => {
      const detail = e.detail || {};
      if (!detail.module || !detail.entityId) return;
      const settings = await getSettings();
      const moduleOn = settings.modules?.[detail.module] !== false;
      if (!moduleOn) return;
      if (!settings.sms_enabled && !settings.email_enabled) return;
      const hasPhone = !!detail.customer?.phone;
      const hasEmail = !!detail.customer?.email;
      // Only prompt if there's at least one channel we can actually use.
      if (!hasPhone && !hasEmail) return;
      setPayload({
        ...detail,
        smsEnabled: settings.sms_enabled && hasPhone,
        emailEnabled: settings.email_enabled && hasEmail,
      });
      setOpen(true);
    };
    window.addEventListener("crm-status-changed", handler);
    return () => window.removeEventListener("crm-status-changed", handler);
  }, []);

  // Reset prompt when settings change elsewhere.
  useEffect(() => {
    const flush = () => { settingsLoadedAt.current = 0; };
    window.addEventListener("crm-status-prompts-updated", flush);
    return () => window.removeEventListener("crm-status-prompts-updated", flush);
  }, []);

  if (!open || !payload) return <div data-testid="status-prompt-mounted" style={{ display: "none" }} />;

  const close = () => { setOpen(false); setPayload(null); };

  const sendSms = () => {
    window.dispatchEvent(new CustomEvent("crm-open-sms", {
      detail: {
        module: payload.module,
        entityId: payload.entityId,
        recipients: payload.customer?.phone ? [payload.customer.phone] : [],
      },
    }));
    close();
  };

  const sendEmail = () => {
    const to = payload.customer?.email || "";
    const subject = `${MODULE_LABELS[payload.module] || payload.module}: změna stavu — ${payload.entityLabel || payload.entityId}`;
    const body = `Dobrý den ${payload.customer?.name || ""},\n\nstav záznamu ${payload.entityLabel || ""} byl změněn na: ${payload.newStatus || ""}.\n\nS pozdravem,\nSolar Green s.r.o.`;
    window.open(`mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    close();
  };

  const moduleLabel = MODULE_LABELS[payload.module] || payload.module;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="status-prompt-dialog"
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95"
      >
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Bell className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 text-base">Stav změněn — informovat zákazníka?</h3>
              <p className="text-xs text-slate-500 mt-1">
                {moduleLabel} <b>{payload.entityLabel || payload.entityId}</b> → nový stav: <b className="text-emerald-700">{payload.newStatus}</b>
              </p>
              {payload.customer?.name && (
                <p className="text-xs text-slate-400 mt-0.5">Zákazník: {payload.customer.name}</p>
              )}
            </div>
            <button onClick={close} className="text-slate-400 hover:text-slate-700" data-testid="status-prompt-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="px-6 py-4 space-y-2">
          {payload.smsEnabled && (
            <button
              onClick={sendSms}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 text-left transition"
              data-testid="status-prompt-send-sms"
            >
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-sm">Odeslat SMS</div>
                <div className="text-[11px] text-slate-500">na {payload.customer.phone}</div>
              </div>
            </button>
          )}
          {payload.emailEnabled && (
            <button
              onClick={sendEmail}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-blue-500 hover:bg-blue-50 text-left transition"
              data-testid="status-prompt-send-email"
            >
              <Mail className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-sm">Odeslat e-mail</div>
                <div className="text-[11px] text-slate-500">na {payload.customer.email}</div>
              </div>
            </button>
          )}
          <button
            onClick={close}
            className="w-full text-xs text-slate-500 hover:text-slate-800 py-2"
            data-testid="status-prompt-skip"
          >
            Neodesílat nic
          </button>
        </div>
      </div>
    </div>
  );
}
